import { defineComponent, reactive, onMounted, watch, computed } from '@vue/composition-api';
import { HiddenAxios } from '@/functions/axios-functions';
import { SettingsContext } from '@/utilities/settings-context';
import Pagination from '@/components/pagination/pagination.vue';
import { debounce } from 'lodash';

// API response types
type CompanyProfileVisibilityResponse = {
  id: number;
  name: string;
  url: string;
  visibilityPercentage: number;
};

type PaginatedSearchResponse = {
  items: CompanyProfileVisibilityResponse[];
  total: number;
};

// Local model type
type CompanyProfileVisibility = CompanyProfileVisibilityResponse & {
  editing: boolean;
  newVisibilityPercentage: number;
};

export default defineComponent({
  components: {
    Pagination,
  },
  setup(_, ctx) {
    const state = reactive({
      items: [] as CompanyProfileVisibility[],
      total: 0,
      loading: false,
    });

    const currentPage = computed({
      get: () => ctx.root.$route.query.page ? parseInt(ctx.root.$route.query.page as string) : 1,
      set: (value: number) => {
        ctx.root.$router.push({ query: { ...(ctx.root.$route.query ?? {}), page: value.toString() } });
      },
    });

    const searchQuery = computed({
      get: () => ctx.root.$route.query.search?.toString() ?? '',
      set: (value: string) => {
        ctx.root.$router.push({ query: { ...(ctx.root.$route.query ?? {}), search: value } });
      },
    });

    const fetchData = async () => {
      state.loading = true;
      const response = await HiddenAxios.get<PaginatedSearchResponse>(SettingsContext.getApiUrl('visibility'), {
        params: {
          search: searchQuery.value,
          page: currentPage.value,
        },
      });
      state.items = response.data.items.map(item => ({
        ...item,
        editing: false, // Initialize editing property
        newVisibilityPercentage: item.visibilityPercentage, // Initialize newVisibilityPercentage property
      }));
      state.total = response.data.total;
      state.loading = false;
    };

    const updateVisibility = async (item: CompanyProfileVisibility, reset = false) => {
      const response = await HiddenAxios.post<number>(`${SettingsContext.getApiUrl('visibility')}/${item.id}`, {
        visibilityPercentage: reset ? null : item.newVisibilityPercentage,
      });

      item.newVisibilityPercentage = response.data;

      item.visibilityPercentage = response.data;

      item.editing = false;
    };

    const buildUrl = (fragment: string) => {
      return `${process.env.VUE_APP_LEDIGAJOBB_WEB}foretag/${fragment}-jobb`;
    };

    onMounted(fetchData);

    watch(() => [currentPage.value, searchQuery.value], debounce(fetchData, 500), { deep: true });

    return {
      state,
      fetchData,
      updateVisibility,
      buildUrl,
      currentPage,
      searchQuery
    };
  },
});